import React, { useState, useMemo } from 'react';
import { Star, ExternalLink, ShoppingBag, Zap } from 'lucide-react';
import { AFFILIATE_PRODUCTS, PLATFORM_INFO } from '../data/affiliateProducts';
import { formatPrice } from '../data/products';

// ── Mini componente de estrellas ─────────────────────────────
function Stars({ rating }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={12}
          fill={i <= Math.round(rating) ? 'var(--orange)' : 'none'}
          color={i <= Math.round(rating) ? 'var(--orange)' : 'var(--gray-400)'}
        />
      ))}
      <span style={{ fontSize: '11px', color: 'var(--gray-400)', marginLeft: '3px' }}>{rating}</span>
    </div>
  );
}

// ── Card de producto afiliado ────────────────────────────────
function AffiliateCard({ product }) {
  const platform = PLATFORM_INFO[product.platform];

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-sm)',
        border: `2px solid ${platform.color}33`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.25s, box-shadow 0.25s',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Imagen */}
      <div style={{ position: 'relative', background: platform.bgColor, overflow: 'hidden' }}>
        <img
          src={product.image}
          alt={product.name}
          style={{
            width: '100%',
            height: '190px',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 0.4s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        />

        {/* Badge plataforma */}
        <span style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          background: platform.color,
          color: platform.textColor,
          fontSize: '10px',
          fontWeight: 800,
          padding: '3px 9px',
          borderRadius: '50px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          {platform.icon} {platform.label}
        </span>



        {/* Etiqueta digital */}
        {product.isDigital && (
          <span style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: '10px',
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: '50px',
          }}>
            📱 Digital
          </span>
        )}
      </div>

      {/* Contenido */}
      <div style={{ padding: '14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: 700,
          color: 'var(--gray-800)',
          lineHeight: 1.3,
        }}>
          {product.name}
        </h3>

        <Stars rating={product.rating} />
        <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
          {product.reviews.toLocaleString('es-CL')} reseñas
        </span>

        {product.description && (
          <p style={{
            fontSize: '12px',
            color: 'var(--gray-600)',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {product.description}
          </p>
        )}

        <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
          {/* Precio */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
            <span style={{ fontSize: '19px', fontWeight: 900, color: 'var(--green-dark)' }}>
              {formatPrice(product.price)}
            </span>
            {product.originalPrice && (
              <span style={{ fontSize: '12px', color: 'var(--gray-400)', textDecoration: 'line-through' }}>
                {formatPrice(product.originalPrice)}
              </span>
            )}
          </div>

          {/* Botón ir a comprar */}
          <a
            href={product.affiliateLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              width: '100%',
              background: platform.color,
              color: platform.textColor,
              border: 'none',
              borderRadius: '10px',
              padding: '10px',
              fontFamily: 'var(--font-main)',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'opacity 0.2s, transform 0.15s',
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.opacity = '0.88';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <ExternalLink size={14} />
            Ver en {platform.label}
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Sección principal ────────────────────────────────────────
const PLATFORM_FILTERS = [
  { id: 'todos', label: 'Todos', icon: '🌐' },
  { id: 'mercadolibre', label: 'Mercado Libre', icon: '🛒' },
  { id: 'amazon', label: 'Amazon', icon: '📦' },
  { id: 'hotmart', label: 'Hotmart', icon: '🔥' },
];

export default function AffiliateSection() {
  const [activePlatform, setActivePlatform] = useState('todos');

  const filtered = useMemo(() => {
    if (activePlatform === 'todos') return AFFILIATE_PRODUCTS;
    return AFFILIATE_PRODUCTS.filter(p => p.platform === activePlatform);
  }, [activePlatform]);

  if (AFFILIATE_PRODUCTS.length === 0) return null;

  return (
    <section style={{
      background: 'linear-gradient(180deg, #f7fdf9 0%, #fff 100%)',
      padding: '60px 0',
      borderTop: '2px solid var(--green-border)',
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <ShoppingBag size={24} color="var(--green-dark)" />
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              color: 'var(--green-dark)',
              margin: 0,
            }}>
              Recomendaciones
            </h2>
          </div>
          <p style={{ color: 'var(--gray-400)', fontSize: '14px', marginBottom: '20px' }}>
            Productos seleccionados de nuestros socios 💚
          </p>

          {/* Filtros de plataforma */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {PLATFORM_FILTERS.map(pf => (
              <button
                key={pf.id}
                onClick={() => setActivePlatform(pf.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  border: `2px solid ${activePlatform === pf.id ? 'var(--green-dark)' : 'var(--green-border)'}`,
                  background: activePlatform === pf.id ? 'var(--green-dark)' : 'white',
                  color: activePlatform === pf.id ? 'white' : 'var(--gray-600)',
                  borderRadius: '50px',
                  fontFamily: 'var(--font-main)',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                  if (activePlatform !== pf.id) {
                    e.currentTarget.style.borderColor = 'var(--green-dark)';
                    e.currentTarget.style.color = 'var(--green-dark)';
                  }
                }}
                onMouseLeave={e => {
                  if (activePlatform !== pf.id) {
                    e.currentTarget.style.borderColor = 'var(--green-border)';
                    e.currentTarget.style.color = 'var(--gray-600)';
                  }
                }}
              >
                <span>{pf.icon}</span> {pf.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid de productos */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
            <p>No hay productos de esta plataforma aún</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '20px',
          }}>
            {filtered.map(product => (
              <AffiliateCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Aviso legal */}
        <p style={{
          fontSize: '11px',
          color: 'var(--gray-400)',
          marginTop: '28px',
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
          * Los precios y disponibilidad pueden variar. Al hacer clic en los links, serás redirigido a la tienda oficial del proveedor.
        </p>
      </div>
    </section>
  );
}
